/**
 * ResetPasswordPage.jsx — /reset-password?token=xxx
 * DeutschBridge Institute
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import API from '../../utils/apiClient';

// ── Brand helpers ─────────────────────────────────────────────
const B = '#1A1A1A';
const R = '#DD0000';
const G = '#FFCE00';

function Flag() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: 28, height: 20, borderRadius: 3, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.3)', flexShrink: 0 }}>
      <div style={{ flex: 1, background: B }} />
      <div style={{ flex: 1, background: R }} />
      <div style={{ flex: 1, background: G }} />
    </div>
  );
}

function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

export default function ResetPasswordPage() {
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const token      = params.get('token');

  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [showPw,    setShowPw]    = useState(false);
  const [showCf,    setShowCf]    = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState(false);
  const [countdown, setCountdown] = useState(3);

  // Count down and redirect after success
  useEffect(() => {
    if (!success) return;
    const id = setInterval(() => {
      setCountdown(p => {
        if (p <= 1) { clearInterval(id); navigate('/login'); return 0; }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [success, navigate]);

  const validate = () => {
    if (password.length < 6) return 'Password must be at least 6 characters.';
    if (password !== confirm)  return 'Passwords do not match.';
    return '';
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setLoading(true);
    try {
      await API.post('/auth/reset-password', { token, newPassword: password });
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    fontSize: 14,
    border: '2px solid #E0E0E0',
    borderRadius: 10,
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };

  // ── Invalid / missing token ────────────────────────────────
  if (!token) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: 'Space Grotesk, sans-serif' }}>
        <div style={{ maxWidth: 440, width: '100%', background: '#fff', borderRadius: 16, border: '1px solid #E0E0E0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ height: 5, background: R }} />
          <div style={{ padding: '40px 36px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: B, marginBottom: 10 }}>Invalid Reset Link</h2>
            <p style={{ fontSize: 14, color: '#777', lineHeight: 1.6, marginBottom: 24 }}>
              This password reset link is missing or invalid. Please request a new one from the login page.
            </p>
            <Link to="/login" style={{ display: 'inline-flex', alignItems: 'center', padding: '12px 28px', background: R, color: '#fff', borderRadius: 10, textDecoration: 'none', fontSize: 14, fontWeight: 700 }}>
              ← Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: 'Space Grotesk, sans-serif' }}>
      <div style={{ maxWidth: 460, width: '100%' }}>

        {/* Card */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E0E0E0', overflow: 'hidden', boxShadow: '0 4px 32px rgba(0,0,0,0.1)' }}>

          {/* Header */}
          <div style={{ background: B, padding: '22px 28px' }}>
            {/* German flag stripe */}
            <div style={{ display: 'flex', height: 4, marginBottom: 18 }}>
              <div style={{ flex: 1, background: B, borderRight: '1px solid rgba(255,255,255,0.1)' }} />
              <div style={{ flex: 1, background: R }} />
              <div style={{ flex: 1, background: G }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Flag />
              <div>
                <span style={{ fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px', fontFamily: 'Syne, sans-serif' }}>
                  <span style={{ color: G }}>Deutsch</span>Bridge
                </span>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', marginLeft: 6 }}>Institute</span>
              </div>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: '32px 28px 28px' }}>

            {success ? (
              /* ── Success state ── */
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#F0FDF4', border: '2px solid #22C55E', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, margin: '0 auto 20px' }}>✅</div>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: B, marginBottom: 8 }}>Password Reset!</h2>
                <p style={{ fontSize: 14, color: '#555', lineHeight: 1.6, marginBottom: 20 }}>
                  Your password has been reset successfully. You can now log in with your new password.
                </p>
                <div style={{ padding: '12px 16px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, marginBottom: 20 }}>
                  <p style={{ fontSize: 13, color: '#15803D', margin: 0, fontWeight: 600 }}>
                    Redirecting to login in <strong>{countdown}</strong> second{countdown !== 1 ? 's' : ''}…
                  </p>
                </div>
                <Link to="/login" style={{ display: 'inline-flex', alignItems: 'center', padding: '12px 28px', background: R, color: '#fff', borderRadius: 10, textDecoration: 'none', fontSize: 14, fontWeight: 700 }}>
                  Go to Login →
                </Link>
              </div>

            ) : (
              /* ── Form state ── */
              <>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: '#FFF0F0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🔑</div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: B, margin: 0 }}>Reset your password</h2>
                  </div>
                  <p style={{ fontSize: 13, color: '#888', margin: 0 }}>
                    Enter a new password for your DeutschBridge account.
                  </p>
                </div>

                {/* Error banner */}
                {error && (
                  <div style={{ padding: '11px 14px', background: '#FFF2F2', border: '1px solid #FECACA', borderRadius: 10, marginBottom: 18, fontSize: 13, color: '#DC2626', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <span style={{ flexShrink: 0 }}>⚠️</span>
                    <span>{error}</span>
                  </div>
                )}

                {/* New password */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: B, marginBottom: 6 }}>
                    New Password *
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={e => { setPassword(e.target.value); setError(''); }}
                      onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                      placeholder="Minimum 6 characters"
                      style={{ ...inputStyle, paddingRight: 44 }}
                      onFocus={e  => e.target.style.borderColor = R}
                      onBlur={e   => e.target.style.borderColor = '#E0E0E0'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(p => !p)}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#999', display: 'flex', alignItems: 'center', padding: 4 }}
                    >
                      <EyeIcon open={showPw} />
                    </button>
                  </div>
                </div>

                {/* Confirm password */}
                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: B, marginBottom: 6 }}>
                    Confirm Password *
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showCf ? 'text' : 'password'}
                      value={confirm}
                      onChange={e => { setConfirm(e.target.value); setError(''); }}
                      onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                      placeholder="Repeat your new password"
                      style={{
                        ...inputStyle,
                        paddingRight: 44,
                        borderColor: confirm && confirm !== password ? '#FECACA' : '#E0E0E0',
                      }}
                      onFocus={e  => e.target.style.borderColor = confirm && confirm !== password ? '#FECACA' : R}
                      onBlur={e   => e.target.style.borderColor = confirm && confirm !== password ? '#FECACA' : '#E0E0E0'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCf(p => !p)}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#999', display: 'flex', alignItems: 'center', padding: 4 }}
                    >
                      <EyeIcon open={showCf} />
                    </button>
                  </div>
                  {confirm && confirm !== password && (
                    <p style={{ margin: '5px 0 0', fontSize: 12, color: '#DC2626' }}>Passwords do not match.</p>
                  )}
                </div>

                {/* Password strength hint */}
                {password.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                      {[...Array(4)].map((_, i) => {
                        const strength = password.length >= 12 ? 4 : password.length >= 8 ? 3 : password.length >= 6 ? 2 : 1;
                        const colors   = ['#EF4444','#F97316','#FFCE00','#22C55E'];
                        return <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i < strength ? colors[strength - 1] : '#E5E7EB', transition: 'background 0.2s' }} />;
                      })}
                    </div>
                    <p style={{ fontSize: 11, color: '#999', margin: 0 }}>
                      {password.length < 6 ? 'Too short' : password.length < 8 ? 'Weak' : password.length < 12 ? 'Good' : 'Strong'}
                    </p>
                  </div>
                )}

                {/* Submit button */}
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{
                    width: '100%', padding: '13px', background: loading ? '#ccc' : R,
                    color: '#fff', border: 'none', borderRadius: 10,
                    fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    transition: 'background 0.2s', minHeight: 48,
                  }}
                  onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#BB0000'; }}
                  onMouseLeave={e => { if (!loading) e.currentTarget.style.background = R; }}
                >
                  {loading ? (
                    <>
                      <svg className="spin" width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 0.8s linear infinite' }}>
                        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeDasharray="40 20" />
                      </svg>
                      Resetting…
                    </>
                  ) : '🔒 Reset Password'}
                </button>

                {/* Back to login */}
                <div style={{ textAlign: 'center', marginTop: 20 }}>
                  <Link to="/login" style={{ fontSize: 13, color: '#888', textDecoration: 'none' }}
                    onMouseEnter={e => e.currentTarget.style.color = R}
                    onMouseLeave={e => e.currentTarget.style.color = '#888'}
                  >
                    ← Back to Login
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer note */}
        <p style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: '#aaa' }}>
          Need help? Call <a href="tel:0202071417" style={{ color: R, textDecoration: 'none' }}>0202071417</a>
        </p>

        {/* Spin keyframe injected inline */}
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
